import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import {
  DeactivatePaymentOption,
  ReactivatePaymentOption,
  GetPaymentOptions,
  PutPaymentOptions,
  TPutPaymentOption,
} from "api/rpc/2024-04/facilityAdmin/facility/paymentOption";
import { GetAccountingReference } from "api/rpc/2024-04/facilityAdmin/facility/accounting";

import { showError } from "redux/actions/ui";
import { IPaymentOption, TAccountingReference } from "redux/reducers/models/transaction";
import { useAppDispatch } from "hooks/redux";
import { handleChangeEventInput, unsavedChangesExist } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { loadPaymentOptions } from "redux/actions/facility";

import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Page from "components/page/Page";
import { Select } from "components/select/index";
import { formatAccountingReferenceLabel } from "utils/formatAccountingReferenceLabel";
import Popup from "components/popup/Popup";
import Portal from "elements/Portal";

export const paymentMethodOptions = [
  { value: "card", label: "Card" },
  { value: "manual", label: "Manual" },
  { value: "manual_card", label: "Manual Card" },
  { value: "account", label: "Account" },
  { value: "gift_card", label: "Gift Card" },
  { value: "credit_book", label: "Credit Book" },
  { value: "invoice", label: "Invoice" },
  { value: "deposit", label: "Deposit" },
] as const;

export default function FacilityPaymentOptionsEdit() {
  const history = useHistory();
  const { t } = useTranslation();
  const { paymentOptionId } = useParams<{ paymentOptionId: string }>();

  const dispatch = useAppDispatch();

  const [paymentOption, setPaymentOption] = useState<TPutPaymentOption>(undefined);
  const [paymentOptionBeforeChanges, setPaymentOptionBeforeChanges] = useState<TPutPaymentOption>(undefined);

  const [accountingReferences, setAccountingReferences] = useState<TAccountingReference[]>([]);
  const { state: modal, updateModal } = useModal();
  const { state: reactivateModal, updateModal: updateReactivateModal } = useModal();

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadPaymentOption(paymentOptionId, source.token);
    void loadAccountingReferences(source.token);
    return () => source.cancel();
  }, [paymentOptionId]);

  async function loadPaymentOption(id: string | number, token?: CancelToken) {
    const res = await GetPaymentOptions({ id: id }, true, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading payment option.")));
    }

    setPaymentOption(res.status !== StatusCode.OK ? undefined : (res.data[0] as IPaymentOption));
    setPaymentOptionBeforeChanges(res.status !== StatusCode.OK ? undefined : (res.data[0] as IPaymentOption));
  }

  async function loadAccountingReferences(token?: CancelToken) {
    const res = await GetAccountingReference(null, true);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading accounting references.")));
    }

    setAccountingReferences(res.status !== StatusCode.OK ? [] : (res.data as TAccountingReference[]));
  }

  async function savePaymentOption() {
    if (paymentOption.id === 0) {
      return;
    }

    const paymentOptionResponse = await PutPaymentOptions(paymentOption, true);

    if (paymentOptionResponse.status !== StatusCode.OK) {
      return;
    }

    history.push("/admin/settings/facility-payment-options");
  }

  async function deactivatePaymentOption() {
    if (paymentOption.id == "-1") {
      return;
    }

    const res = await DeactivatePaymentOption({ id: paymentOption.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deactivating the payment option.")));
      return;
    }

    void dispatch(loadPaymentOptions(undefined, true));
    updateModal({ isOpen: false });
    history.push("/admin/settings/facility-payment-options");
  }

  async function reactivatePaymentOption() {
    if (paymentOption.id == "-1") {
      return;
    }

    const res = await ReactivatePaymentOption({ id: paymentOption.id }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error reactivating the payment option.")));
      return;
    }

    void dispatch(loadPaymentOptions(undefined, true));
    updateModal({ isOpen: false });
    history.push("/admin/settings/facility-payment-options");
  }

  const primaryAction = {
    content: paymentOption?.active ? t("Deactivate") : t("Reactivate"),
    action: () => (paymentOption?.active ? updateModal({ isOpen: true }) : updateReactivateModal({ isOpen: true })),
  };

  return (
    <Page
      title={`${t("Edit Payment Method")} ${paymentOptionBeforeChanges ? paymentOptionBeforeChanges.title : ""}`}
      narrow
      primaryAction={primaryAction}
      breadcrumbs={[
        {
          label: t("Payment Methods"),
          url: "/admin/settings/facility-payment-options",
        },
      ]}
      notificationBarProps={{
        isVisible:
          unsavedChangesExist(paymentOption, paymentOptionBeforeChanges) &&
          paymentOption.accounting_reference_id !== "-1",
        onAction: savePaymentOption,
        onCancel: () => setPaymentOption(paymentOptionBeforeChanges),
      }}
    >
      <Form>
        <Card>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={paymentOption?.title || ""}
                  label={t("Title")}
                  id="title"
                  onChange={e => handleChangeEventInput(e, paymentOption, setPaymentOption)}
                  placeholder={t("Title")}
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("Payment Method")}
                  defaultValue={paymentOption?.type ?? paymentMethodOptions[0].value}
                  onChange={(value: string) => setPaymentOption(prev => ({ ...prev, type: value }))}
                >
                  {paymentMethodOptions.map(paymentMethodOption => (
                    <Select.Option
                      key={paymentMethodOption.value}
                      value={paymentMethodOption.value}
                      name={t(paymentMethodOption.label)}
                    >
                      {t(paymentMethodOption.label)}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("Accounting Reference")}
                  defaultValue={paymentOption ? paymentOption.accounting_reference_id : "-1"}
                  onChange={(value: string) => setPaymentOption(prev => ({ ...prev, accounting_reference_id: value }))}
                  error={
                    accountingReferences.length > 0 && paymentOption?.accounting_reference_id === "-1" ? true : false
                  }
                >
                  <Select.Option key={"none"} value={"-1"} name="none">
                    <div style={{ minHeight: "32px" }}></div>
                  </Select.Option>
                  {accountingReferences.map((type, index: number) => {
                    const label = formatAccountingReferenceLabel(type.title ?? "", type.reference_number);
                    return (
                      <Select.Option key={index} value={type.id} name={label}>
                        <span>{label}</span>
                      </Select.Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>
      </Form>

      <Portal isMounted={modal.isOpen}>
        <Popup
          open={modal.isOpen}
          type={"warning"}
          title={t("Deactivate Payment Method")}
          description={`${t("You are about to deactivate")} ${paymentOptionBeforeChanges?.title} ${t(
            "as a payment option.  Are you sure?",
          )}`}
          onOk={() => deactivatePaymentOption()}
          onCancel={() => updateModal({ isOpen: false })}
        />
      </Portal>

      <Portal isMounted={reactivateModal.isOpen}>
        <Popup
          open={reactivateModal.isOpen}
          type={"warning"}
          title={t("Reactivate Payment Method")}
          description={`You are about to reactivate ${paymentOptionBeforeChanges?.title} as a payment option. Are you sure?`}
          onOk={() => reactivatePaymentOption()}
          onCancel={() => updateReactivateModal({ isOpen: false })}
        />
      </Portal>
    </Page>
  );
}
